import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import type { ServiceDay, OffDay, MonthScheduleData } from '@types'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Colors
const BLUE = '#90CAF9' // has menus/orders
const GREEN = '#66bb6a' // service expected, no menu yet
const RED = '#ef5350' // day off
const SELECTED_RING = '#dbf517'

interface Props {
  /** Currently selected date as ISO string (YYYY-MM-DD) */
  selectedDate: string
  /** Map of ISO date strings that have date menus */
  menuDates: Set<string>
  /** Called when user clicks a selectable date */
  onSelectDate: (isoDate: string) => void
  /** Called when viewed month changes — parent should fetch schedule data */
  onMonthChange: (year: number, month: number) => void
  /** Schedule data for the currently viewed month */
  scheduleData: MonthScheduleData | null
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(
    2,
    '0',
  )}`
}

function format12hr(time: number): string {
  const hours24 = Math.floor(time / 100)
  const minutes = time % 100
  const ampm = hours24 >= 12 ? 'pm' : 'am'
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24
  const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''

  return `${hours12}${minuteStr}${ampm}`
}

/** Mon=0 offset for the 1st of the month */
function getStartOffset(year: number, month: number): number {
  const dow = new Date(year, month, 1).getDay() // 0=Sun

  return dow === 0 ? 6 : dow - 1
}

const ScheduleCalendar: React.FC<Props> = ({
  selectedDate,
  menuDates,
  onSelectDate,
  onMonthChange,
  scheduleData,
}) => {
  // Viewed month (independent of selectedDate so prev/next work freely)
  const selParts = selectedDate.split('-').map(Number)
  const [viewYear, setViewYear] = useState(selParts[0])
  const [viewMonth, setViewMonth] = useState(selParts[1] - 1) // 0-indexed

  // Build lookup maps from schedule data
  const serviceDaysMap = useMemo(() => {
    const m = new Map<string, ServiceDay>()
    if (scheduleData) {
      for (const sd of scheduleData.serviceDays) {
        m.set(sd.date, sd)
      }
    }

    return m
  }, [scheduleData])

  const offDaysMap = useMemo(() => {
    const m = new Map<string, OffDay>()
    if (scheduleData) {
      for (const od of scheduleData.offDays) {
        m.set(od.date, od)
      }
    }

    return m
  }, [scheduleData])

  const todayStr = useMemo(() => {
    const t = new Date()

    return toISO(t.getFullYear(), t.getMonth(), t.getDate())
  }, [])

  const goPrev = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1)

        return 11
      }

      return m - 1
    })
  }, [])

  const goNext = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1)

        return 0
      }

      return m + 1
    })
  }, [])

  // Notify parent when viewed month changes
  useEffect(() => {
    onMonthChange(viewYear, viewMonth)
  }, [viewYear, viewMonth, onMonthChange])

  // Build day cells
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const startOffset = getStartOffset(viewYear, viewMonth)

  const cells: React.ReactNode[] = []

  for (let i = 0; i < startOffset; i++) {
    cells.push(<EmptyCell key={`e-${i}`} />)
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const iso = toISO(viewYear, viewMonth, d)
    const hasMenu = menuDates.has(iso)
    const serviceDay = serviceDaysMap.get(iso)
    const offDay = offDaysMap.get(iso)
    const isSelected = iso === selectedDate
    const isToday = iso === todayStr

    let dayType: 'menu' | 'service' | 'off' | 'normal' = 'normal'
    if (hasMenu) {
      dayType = 'menu'
    } else if (offDay) {
      dayType = 'off'
    } else if (serviceDay) {
      dayType = 'service'
    }

    cells.push(
      <DayCell
        key={iso}
        data-testid={`cal-day-${iso}`}
        dayType={dayType}
        isSelected={isSelected}
        isToday={isToday}
        clickable={true}
        onClick={() => onSelectDate(iso)}
      >
        <DayNumber isToday={isToday}>{d}</DayNumber>
        {/* Popover for service-expected days */}
        {dayType === 'service' && serviceDay && (
          <Popover
            className="schedule-popover"
            data-testid={`popover-service-${iso}`}
          >
            <PopoverTitle>{serviceDay.scheduleName}</PopoverTitle>
            {serviceDay.dropoffTime != null && (
              <PopoverRow>
                Dropoff: {format12hr(serviceDay.dropoffTime)}
              </PopoverRow>
            )}
            {serviceDay.cutoffDaysBefore != null &&
              serviceDay.cutoffTime != null && (
                <PopoverRow>
                  Cutoff: {serviceDay.cutoffDaysBefore}d before @{' '}
                  {format12hr(serviceDay.cutoffTime)}
                </PopoverRow>
              )}
          </Popover>
        )}
        {/* Popover for off days */}
        {dayType === 'off' && offDay && (
          <Popover
            className="schedule-popover"
            data-testid={`popover-off-${iso}`}
          >
            <PopoverTitle>{offDay.scheduleName}</PopoverTitle>
            {offDay.reason && <PopoverRow>Reason: {offDay.reason}</PopoverRow>}
            {offDay.notes && <PopoverRow>{offDay.notes}</PopoverRow>}
          </Popover>
        )}
      </DayCell>,
    )
  }

  return (
    <Container>
      <Header>
        <NavButton onClick={goPrev} data-testid="cal-prev">
          &lsaquo;
        </NavButton>
        <MonthLabel data-testid="cal-month-label">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </MonthLabel>
        <NavButton onClick={goNext} data-testid="cal-next">
          &rsaquo;
        </NavButton>
      </Header>
      <Grid>
        {DAY_HEADERS.map((h) => (
          <DayHeader key={h}>{h}</DayHeader>
        ))}
        {cells}
      </Grid>
    </Container>
  )
}

export default ScheduleCalendar

// ── Styled Components ──

const Container = styled.div`
  width: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const NavButton = styled.button`
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 20px;
  line-height: 1;
  padding: 2px 10px;
  cursor: pointer;
  color: #424242;
  &:hover {
    background: #f5f5f5;
  }
`

const MonthLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #212121;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  overflow: visible;
`

const DayHeader = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  padding: 4px 0;
`

const EmptyCell = styled.div`
  min-height: 36px;
`

const Popover = styled.div`
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #424242;
  color: #fff;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 300;
  pointer-events: none;
  width: max-content;
  max-width: 220px;
  text-align: left;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #424242;
  }
`

const PopoverTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: #fff;
`

const PopoverRow = styled.div`
  line-height: 1.4;
  color: #fff;
`

function bgForDayType(dayType: string): string {
  switch (dayType) {
    case 'menu':
      return BLUE
    case 'service':
      return GREEN
    case 'off':
      return RED
    default:
      return '#fff'
  }
}

const DayCell = styled.div<{
  dayType: string
  isSelected: boolean
  isToday: boolean
  clickable: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 4px 2px;
  border-radius: 4px;
  overflow: visible;
  background: ${({ dayType }) => bgForDayType(dayType)};
  border: ${({ isSelected }) =>
    isSelected ? `3px solid ${SELECTED_RING}` : '3px solid transparent'};
  outline: ${({ isSelected }) =>
    isSelected ? `2px solid ${SELECTED_RING}` : 'none'};
  outline-offset: 1px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: opacity 0.15s;

  ${({ isToday }) =>
    isToday &&
    `
    font-weight: 700;
  `}

  ${({ clickable }) =>
    clickable &&
    `
    &:hover { opacity: 0.8; }
  `}

  &:hover > .schedule-popover {
    display: block;
  }
`

const DayNumber = styled.span<{ isToday: boolean }>`
  font-size: 13px;
  font-weight: ${({ isToday }) => (isToday ? 700 : 400)};
  color: ${({ isToday }) => (isToday ? '#1976D2' : '#424242')};
  line-height: 1.3;
`
